import { useLocation } from 'react-router';
import { Widget } from '@optiprime/referral-widget';
import { useOptibotStore } from '@/features/optibot/stores/optibot-store';
import { useReferralAccess } from './hooks/useReferralAccess';
import { useReferralClient, useReferralLink, useReferralPrompt } from './useReferralClient';
import { OPTISIGNS_THEME, useReferralT } from './referral-theme';
import { useReferralCoachmarkStore } from './referral-coachmark-store';
import { ReferralCoachmark } from './ReferralCoachmark';
import {
  analyticsReferralWidgetShown,
  analyticsReferralPillOpened,
  analyticsReferralPillDismissed,
  analyticsReferralModalAutoOpened,
  analyticsReferralInviteSent,
  analyticsReferralLinkCopied,
} from '@/lib/analytics-service';

// Stack the referral pill directly above the OptiBot chat FAB in the bottom-right
// corner. The FAB is a 60px circle sitting 24px off the bottom; lift the pill by
// the FAB height plus a 12px gap so the two read as one tidy column. Derived from
// the FAB's own dimensions — not a magic number — so a FAB resize can't silently
// make them overlap. (Pill bottom = its own 24px base + this shift = 96px.)
const OPTIBOT_FAB_HEIGHT = 60;
const LAUNCHER_GAP = 12;
const REFERRAL_PILL_SHIFT = OPTIBOT_FAB_HEIGHT + LAUNCHER_GAP;

/**
 * Mounts the portable referral `<Widget>` (Pill → Modal → Dashboard) in the
 * AppShell, next to the OptiBot FAB. All gating (feature flag, iframe context,
 * subscription eligibility) is delegated to `useReferralAccess()` — this
 * component simply returns null when `entryEnabled` is false.
 *
 * The widget talks to the referral ENGINE via `useReferralClient()` (its own
 * SDK, NOT scio3 GraphQL) and is seeded with `preloadedCode` from the local Me
 * mirror so the referrer sees their code without an extra round-trip.
 *
 * i18n: the widget ships its own English `defaultT`. We pass a thin `refT`
 * adapter that prefers scio3's `referral.*` keys and falls THROUGH to the
 * widget's English default when a key is absent — so it renders correctly today
 * even though the host `referral.*` keys arrive in the next increment (T149).
 *
 * onDismiss: when the user first dismisses the floating pill, show a once-ever
 * coachmark anchored near the profile menu (top-right) pointing them to
 * "Refer & Earn lives here".
 */
export function ReferralWidgetMount() {
  const client = useReferralClient();
  const { referralCode } = useReferralLink();
  const refT = useReferralT();
  // Server-driven expansion prompt: open the modal once when the user just hit
  // an expansion moment (first paid / first expansion), then clear it.
  const { pending: referralPromptPending, clearPrompt } = useReferralPrompt();
  const showCoachmark = useReferralCoachmarkStore((s) => s.show);
  const optibotOpen = useOptibotStore((s) => s.isOpen);
  // The Get Started checklist sits in the same bottom-right corner (above the
  // FAB), so the pill overlaps its panel. The checklist already publishes its
  // on-screen visibility to the OptiBot store (setChecklistOpen), so we read it
  // here and animate the pill OUT while the checklist shows — the corner holds
  // one thing at a time (same treatment as an open chat). It returns when the
  // user minimizes/dismisses the checklist.
  const checklistOpen = useOptibotStore((s) => s.checklistOpen);
  // Hide the floating launcher while the user is already ON the Refer & Earn page
  // (no point nudging them to a page they're on). The Widget stays mounted — only
  // the pill is hidden via CSS — so the user's own dismiss state is preserved when
  // they leave the page.
  const onReferralPage = useLocation().pathname.startsWith('/app/s/referral');
  const { entryEnabled } = useReferralAccess();

  if (!entryEnabled) return null;

  // While OptiBot is open the launcher animates OUT (fade + scale via CSS, keyed
  // on `data-chat-open`) rather than unmounting. Keeping it mounted means on
  // close it returns IN SYNC with the chat FAB and animated — not lagging behind
  // the widget's me()/config() re-fetch a remount would trigger. The secondary
  // styling, centering, and this transition all live in referral-widget-overrides.css.
  return (
    <div
      data-referral-launcher
      data-chat-open={optibotOpen}
      data-checklist-open={checklistOpen}
      data-on-referral-page={onReferralPage}
    >
      <Widget
        client={client}
        productId="optisigns"
        preloadedCode={referralCode}
        placement="bottom-right"
        collisionShift={REFERRAL_PILL_SHIFT}
        theme={OPTISIGNS_THEME}
        t={refT}
        autoOpen={!!referralPromptPending}
        onShown={analyticsReferralWidgetShown}
        onOpen={analyticsReferralPillOpened}
        onAutoOpened={() => {
          // Capture the prompt type BEFORE clearPrompt() nulls it out.
          analyticsReferralModalAutoOpened({ promptType: referralPromptPending });
          clearPrompt();
        }}
        onDismiss={() => {
          analyticsReferralPillDismissed();
          showCoachmark();
        }}
        onInviteSent={(count) => analyticsReferralInviteSent({ surface: 'pill_modal', count })}
        onCodeCopied={() => analyticsReferralLinkCopied({ surface: 'pill_modal' })}
      />
      <ReferralCoachmark />
    </div>
  );
}
